<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol
                sm="12"
                class="text-right"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <CollectionFilter
            v-model:invoice-id="filter.invoiceId"
            v-model:customer-id="filter.customerId"
            v-model:customer-search="filter.customerSearch"
            v-model:invoice-price-operator="filter.invoicePriceOperator"
            v-model:invoice-price="filter.invoicePrice"
            v-model:exclude-invoices-with-open-incidents="filter.excludeInvoicesWithOpenIncidents"
            v-model:currency="filter.currency"
            :currency-codes="currencyCodes"
            @filter="applyFilter"
        />
        <DTable
            :key="tableKey"
            :items="form.invoices"
            :fields="getFields"
            class="table-responsive table-sticky-header"
        >
            <template #lawsuit-header>
                <div class="header-actions">
                    {{ __('tools.in_collection.headers.lawsuit') }}
                    <DFormCheck
                        id="header_lawsuit"
                        v-model="headerActions.lawsuit"
                        inline
                        type="radio"
                        :name="'header'"
                        :checked="headerActions.lawsuit"
                        @click="onClickAllActions('lawsuit')"
                    />
                </div>
            </template>
            <template #debit_loss-header>
                <div class="header-actions">
                    {{ __('tools.in_collection.headers.debit_loss') }}
                    <DFormCheck
                        id="header_debit_loss"
                        v-model="headerActions.debit_loss"
                        inline
                        type="radio"
                        :name="'header'"
                        :checked="headerActions.debit_loss"
                        @click="onClickAllActions('debit_loss')"
                    />
                </div>
            </template>
            <template #ignore-header>
                <div class="header-actions">
                    {{ __('tools.in_collection.headers.ignore') }}
                    <DFormCheck
                        id="ignore"
                        v-model="headerActions.ignore"
                        inline
                        type="radio"
                        :name="'header'"
                        :checked="headerActions.ignore"
                        @click="onClickAllActions('ignore')"
                    />
                </div>
            </template>
            <template #lawsuit="{item}">
                <td>
                    <DFormCheck
                        :id="`action_${item.id}lawsuit`"
                        v-model="item.lawsuit"
                        inline
                        type="radio"
                        :name="item.id"
                        :checked="item.lawsuit"
                        @change="onChangeActions(item, 'lawsuit')"
                    />
                </td>
            </template>
            <template #debit_loss="{item}">
                <td>
                    <DFormCheck
                        :id="`action_${item.id}_debit_loss`"
                        v-model="item.debit_loss"
                        inline
                        type="radio"
                        :name="item.id"
                        :checked="item.debit_loss"
                        @change="onChangeActions(item, 'debit_loss')"
                    />
                </td>
            </template>
            <template #ignore="{item}">
                <td>
                    <DFormCheck
                        :id="`action_${item.id}_ignore`"
                        v-model="item.ignore"
                        inline
                        type="radio"
                        :name="item.id"
                        :checked="item.ignore"
                        @change="onChangeActions(item, 'ignore')"
                    />
                </td>
            </template>
            <template #id="{item}">
                <td v-if="userPermissions.includes('invoice.view')">
                    <Link :href="showInvoiceRoute(item)">
                        {{ item.id }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.id }}
                </td>
            </template>
            <template #customer_firstname="{item}">
                <td>{{ item.customer_firstname }}</td>
            </template>
            <template #customer_lastname="{item}">
                <td>{{ item.customer_lastname }}</td>
            </template>
            <template #customer_company="{item}">
                <td>{{ item.customer_company }}</td>
            </template>
            <template #customer_id="{item}">
                <td v-if="userPermissions.includes('customer.view')">
                    <Link :href="customerInvoicesRoute(item)">
                        {{ item.customer_id }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.customer_id }}
                </td>
            </template>
            <template #total_amount="{item}">
                <td>{{ roundPrice(item.total_amount, item.customer.market_region_id) }}</td>
            </template>
            <template #open_item_amount="{item}">
                <td>{{ roundPrice(item.open_item_amount, item.customer.market_region_id) }}</td>
            </template>
            <template #date="{item}">
                <td>{{ setDateFormat(item.date) }}</td>
            </template>
            <template #sanity="{item}">
                <td>{{ __('customers.common.sanity.' + item.customer.sanity) }}</td>
            </template>
            <template #products="{item}">
                <td>
                    <div
                        v-for="(product, index) in item.products"
                        :key="index"
                    >
                        {{ product }}
                    </div>
                </td>
            </template>
        </DTable>
        <DFabs>
            <div>
                <DFormTextarea
                    v-if="checkedActionsData.lawsuit && checkedActionsData.lawsuit.length > 0 || headerActions.lawsuit === true"
                    v-model="form.lawsuit_sticky_note"
                    class="lawsuit_sticky_note"
                    :placeholder="__('tools.in_collection.lawsuit_sticky_note')"
                    add-input-classes="bg-light"
                    :is-valid="isFormFieldValid('lawsuit_sticky_note')"
                    maxlength="1000"
                />
            </div>
            <DFab
                id="btn-add"
                icon="file-export"
                :label="__('tools.in_collection.save_dunning_status')"
                @click="handoverCollections"
            />
        </DFabs>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="closeListViewModal()"
        />
    </DCard>
</template>

<script>
import CollectionFilter from '~@/Components/Tools/Collection/Filter.vue'
import ListViewModal from '~@/Components/ListView/ListViewModal'

export default {
    name: 'InCollectionIndex',
    components: { CollectionFilter, ListViewModal },
    props: {
        invoices: {
            type: Array,
            default: () => { return [] }
        },
        appliedFilters: {
            type: Object,
            default: () => {
                return {
                    invoiceId: null,
                    customerId: null,
                    customerSearch: null,
                    invoicePriceOperator: 'greater',
                    invoicePrice: null,
                    excludeInvoicesWithOpenIncidents: false,
                    currency: null
                }
            }
        },
        checkedActions: {
            type: Object,
            default: () => { return {} }
        },
        headerCheckedActions: {
            type: Object,
            default: () => { return {} }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        currencyCodes: {
            type: Array,
            default: () => { return [] }
        }
    },
    data () {
        return {
            exclusion: {},
            form: this.$inertia.form({
                invoices: this.invoices,
                lawsuit_sticky_note: null
            }),
            filter: {
                invoiceId: this.appliedFilters.invoiceId,
                customerId: this.appliedFilters.customerId,
                customerSearch: this.appliedFilters.customerSearch,
                invoicePrice: this.appliedFilters.invoicePrice,
                invoicePriceOperator: this.appliedFilters.invoicePriceOperator === undefined ? 'greater' : this.appliedFilters.invoicePriceOperator,
                excludeInvoicesWithOpenIncidents: this.appliedFilters.excludeInvoicesWithOpenIncidents,
                currency: this.appliedFilters.currency
            },
            headerActions: {
                lawsuit: this.headerCheckedActions['lawsuit-header'] ?? false,
                debit_loss: this.headerCheckedActions['debit_loss-header'] ?? false,
                ignore: this.headerCheckedActions['ignore-header'] ?? false
            },
            checkedActionsData: this.checkedActions,
            tableKey: 0,
            showListViewModal: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        getFields () {
            const availableList = [
                { key: 'lawsuit', label: '', _style: { minWidth: '10%' } },
                { key: 'debit_loss', label: '', _style: { minWidth: '10%' } },
                { key: 'ignore', label: '', _style: { minWidth: '10%' } },
            ]

            this.userListView.forEach((item) => {
                if (item.show_column === false) {
                    return
                }

                availableList.push({
                    key: item.column,
                    label: this.__('settings.list_view.' + item.list + '.' + item.column),
                    _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                })
            })

            return availableList
        }
    },
    watch: {
        invoices () {
            this.form = this.$inertia.form({
                invoices: this.invoices,
                lawsuit_sticky_note: null
            })
        }
    },
    created () {
        this.setPreselectedActions()
    },
    methods: {
        handoverCollections () {
            this.$inertia.post(this.$route('tools.in-collection.save-dunning-status'), {
                lawsuit: this.form
                    .invoices
                    .filter((invoice) => invoice.lawsuit === true)
                    .map((invoice) => invoice.id),
                debt_loss: this.form
                    .invoices
                    .filter((invoice) => invoice.debit_loss === true)
                    .map((invoice) => invoice.id),
                lawsuit_sticky_note: this.form.lawsuit_sticky_note
            }, {
                onError: () => {
                    this.setPreselectedActions()
                },
                onSuccess: () => {
                    this.setPreselectedActions()
                    ++this.tableKey
                }
            })
        },
        roundPrice (price, marketRegionId = null) {
            return price ? this.priceFormatForDisplay(price, 2, marketRegionId) : ''
        },
        showInvoiceRoute (invoice) {
            return this.$route('customers.customers.invoices.show', {
                customer: invoice.customer.id,
                invoice: invoice.id
            })
        },
        customerInvoicesRoute (invoice) {
            return this.$route('customers.customers.invoices.index', { customer: invoice.customer.id })
        },
        applyFilter (defaultFilters = false) {
            // const queryParameters = Object.fromEntries(Object.entries(this.filter).filter(([_, v]) => v))
            const queryParameters = Object.fromEntries(
                Object.entries(this.filter).filter(([_, v]) => v !== undefined && v !== null)
                    .map(([key, value]) => {
                        if (typeof value === 'boolean') {
                            return [key, value ? '1' : '0']
                        }
                        return [key, value]
                    })
            )

            const radioButtonsData = {
                lawsuit: [],
                debit_loss: [],
                ignore: [],
                'lawsuit-header': 'false',
                'debit_loss-header': 'false',
                'ignore-header': 'false'
            }

            for (const [key, arrayOfIds] of Object.entries(this.checkedActionsData)) {
                radioButtonsData[key] = arrayOfIds
            }

            for (const [key, value] of Object.entries(this.headerActions)) {
                radioButtonsData[`${key}-header`] = value === true ? 'true' : 'false'
            }

            this.$inertia.get(
                this.$route('tools.in-collection.index', {
                    _query: queryParameters,
                    saveFilters: true,
                    defaultFilters: defaultFilters
                }),
                radioButtonsData,
                {
                    onSuccess: () => {
                        this.setPreselectedActions()
                    }
                }
            )
        },
        onChangeActions (activeInvoice, activeAction) {
            // Matches the value of the radio button that got checked, set to false all other
            this.form.invoices.forEach((invoice) => {
                if (invoice.id === activeInvoice.id) {
                    invoice.lawsuit = activeAction === 'lawsuit'
                    invoice.debit_loss = activeAction === 'debit_loss'
                    invoice.ignore = activeAction === 'ignore'

                    return false
                }
            })

            // Add or remove invoice id for specific action, we use checkedActionsData for preselecting radio buttons when use Filter
            for (const [key, arrayOfIds] of Object.entries(this.checkedActionsData)) {
                this.form.invoices.forEach((invoice) => {
                    if (invoice.id === activeInvoice.id) {
                        // Set new value for clicked button
                        if (key === activeAction) {
                            // Add invoice id in specific action array
                            if (arrayOfIds.includes(invoice.id) === false) {
                                this.checkedActionsData[key].push(invoice.id)
                            }
                        } else {
                            // Remove invoice id in specific action array
                            if (arrayOfIds.includes(invoice.id) === true) {
                                const idIndexOf = this.checkedActionsData[key].indexOf(invoice.id)
                                this.checkedActionsData[key].splice(idIndexOf, 1)
                            }
                        }
                    }
                })
            }

            this.headerActions.lawsuit = false
            this.headerActions.debit_loss = false
            this.headerActions.ignore = false
        },
        onClickAllActions (action) {
            const newValue = !this.headerActions[action]

            // Can not uncheck all ignore action
            if (action === 'ignore' && newValue === false) {
                return
            }

            this.headerActions.lawsuit = action === 'lawsuit' ? newValue : false
            this.headerActions.debit_loss = action === 'debit_loss' ? newValue : false
            this.headerActions.ignore = action === 'ignore' ? newValue : false

            // Matches the value of the radio button that got checked for all invoices, set to false all other invoice actions
            this.form.invoices.forEach((invoice) => {
                invoice.lawsuit = action === 'lawsuit' && newValue === true
                invoice.debit_loss = action === 'debit_loss' && newValue === true
                invoice.ignore = action === 'ignore' && newValue === true

                // If newValue is false set all ignore invoice buttons to true, that is default value
                if (newValue === false) {
                    invoice.ignore = true
                }
            })

            // Add or remove invoice id for specific action, we use checkedActionsData for preselecting radio buttons when use Filter
            for (const [key, arrayOfIds] of Object.entries(this.checkedActionsData)) {
                this.form.invoices.forEach((invoice) => {
                    if (newValue === true) {
                        // Set new value for clicked button
                        if (key === action) {
                            // Add invoice id in specific action array
                            if (arrayOfIds.includes(invoice.id) === false) {
                                this.checkedActionsData[key].push(invoice.id)
                            }
                        } else {
                            // Remove invoice id in specific action array
                            if (arrayOfIds.includes(invoice.id) === true) {
                                const idIndexOf = this.checkedActionsData[key].indexOf(invoice.id)
                                this.checkedActionsData[key].splice(idIndexOf, 1)
                            }
                        }
                    } else { // Set all ids to ignore array
                        if (key === 'ignore') {
                            // Add invoice id in specific action array
                            if (arrayOfIds.includes(invoice.id) === false) {
                                this.checkedActionsData[key].push(invoice.id)
                            }
                        } else {
                            // Remove invoice id in specific action array
                            if (arrayOfIds.includes(invoice.id) === true) {
                                const idIndexOf = this.checkedActionsData[key].indexOf(invoice.id)
                                this.checkedActionsData[key].splice(idIndexOf, 1)
                            }
                        }
                    }
                })
            }
            // Need to increase table key to refresh radio buttons data
            ++this.tableKey
        },
        setPreselectedActions  () {
            // Set invoice actions
            this.form.invoices.forEach((invoice) => {
                let checked = false
                let checkIgnore = true
                for (const [key, arrayOfIds] of Object.entries(this.checkedActionsData)) {
                    checked = arrayOfIds.includes(invoice.id) === true
                    invoice[key] = checked

                    if (checked) {
                        checkIgnore = false
                    }
                }

                if (checkIgnore) {
                    invoice.ignore = true
                }
            })

            // Set header actions
            this.headerActions.lawsuit = this.headerCheckedActions['lawsuit-header']
            this.headerActions.debit_loss = this.headerCheckedActions['debit_loss-header']
            this.headerActions.ignore = this.headerCheckedActions['ignore-header']
        },
        closeListViewModal () {
            this.showListViewModal = false
            ++this.tableKey
        }
    }
}
</script>

<style lang="scss" scoped>
.header-actions {
    display: grid;
    justify-items: flex-start;
    gap: 10px;
}
</style>
